/**
 * Section « À propos et maintenance » de l'onglet Import et paramètres —
 * EF-NAV-004, EF-POI-007, EF-POI-009, EF-POI-016, EF-PLT-003 à EF-PLT-005,
 * EF-FIC-003, EF-MOD-008, section 3.3.
 *
 * Elle porte l'attribution OpenStreetMap, qui ne figure nulle part ailleurs :
 * répétée sur les écrans de travail, elle occupait une place utile à
 * l'information de course sans rien apporter.
 */

import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { OVERPASS_ENDPOINTS, endpointById, type EndpointId } from '../../overpass/endpoints';
import { estimateUsage } from '../../store/database';
import { formatBytes, persistenceMessage } from '../../platform/persistence';
import { ACTIONS, APP_NAME, CONFIRMATIONS, KNOWN_LIMITS, NOTICES } from '../labels';
import {
  APP_VERSION,
  activateUpdate,
  checkForUpdate,
  document_,
  endpointId,
  lastExport,
  offlineReport,
  openSheet,
  parameters,
  pendingConfirmation,
  persistence,
  readOnly,
  resetEverything,
  resetParameters,
  resetRoadbook,
  retryOfflinePreparation,
  setEndpoint,
  setParameters,
  updateState,
} from '../state';

export function MaintenanceSection(): JSX.Element {
  const [usage, setUsage] = useState<{ usageBytes: number | null; quotaBytes: number | null }>({
    usageBytes: null,
    quotaBytes: null,
  });

  useEffect(() => {
    void estimateUsage().then(setUsage);
  }, []);

  const offline = offlineReport.value;
  const update = updateState.value;
  const exported = lastExport.value;
  const selected = endpointId.value;
  const endpoint = endpointById(selected);
  const current = document_.value;

  let absent = 0;
  if (current !== null) {
    for (const poi of current.pois.values()) {
      if (poi.userState.presenceState === 'absentFromResult') absent += 1;
    }
  }

  const confirmReset = (title: string, body: string, action: () => Promise<void>): void => {
    pendingConfirmation.value = {
      title,
      body,
      confirmLabel: 'Réinitialiser',
      offerExport: current !== null,
      onConfirm: action,
    };
  };

  return (
    <>
      <section class="card">
        <h3 style="margin-top:0;">Purge des POI obsolètes</h3>
        <p class="muted">
          {absent === 0
            ? 'Aucun POI n’est marqué « absent du dernier résultat ».'
            : `${absent} POI sont marqués « absent du dernier résultat ».`}{' '}
          La purge ne porte que sur les POI non curés et ouvre une fenêtre d’annulation.
        </p>
        <div class="actions">
          <button
            type="button"
            disabled={readOnly.value || current === null}
            onClick={() => {
              openSheet('purge');
            }}
          >
            {ACTIONS.purge}
          </button>
        </div>
      </section>

      <section class="card">
        <h3 style="margin-top:0;">Endpoint Overpass</h3>
        <p class="muted">{NOTICES.endpointNotice}</p>
        <p class="muted">{NOTICES.transmittedData}</p>
        {OVERPASS_ENDPOINTS.map((entry) => (
          <label class="checkbox" key={entry.id}>
            <input
              type="radio"
              name="endpoint"
              checked={selected === entry.id}
              onChange={() => {
                void setEndpoint(entry.id as EndpointId);
              }}
            />
            <span>
              <strong>{entry.operator}</strong>
              <br />
              <span class="muted">{entry.note}</span>
              {entry.warning !== null ? (
                <>
                  <br />
                  <span class="muted">{entry.warning}</span>
                </>
              ) : null}
            </span>
          </label>
        ))}
        <p class="muted">
          Un endpoint devenu indisponible ne bascule jamais automatiquement vers l’autre : le choix reste
          explicite.
        </p>
      </section>

      <section class="card">
        <h3 style="margin-top:0;">Écran</h3>
        <label class="checkbox">
          <input
            type="checkbox"
            checked={parameters.value.device.keepScreenAwake}
            onChange={(event) => {
              void setParameters({
                ...parameters.value,
                device: { keepScreenAwake: (event.currentTarget as HTMLInputElement).checked },
              });
            }}
          />
          <span>Garder l’écran allumé en mode course</span>
        </label>
        <p class="muted">
          Ce réglage est désactivé par défaut en raison de son effet sur la batterie. L’état réel du verrou
          est affiché en mode course.
        </p>
      </section>

      <section class="card">
        <h3 style="margin-top:0;">Stockage et disponibilité hors ligne</h3>
        <dl class="definition-list">
          <div>
            <dt>Disponibilité hors ligne</dt>
            <dd>{offline.status}</dd>
          </div>
          <div>
            <dt>Persistance</dt>
            <dd>
              {persistence.value} — {persistenceMessage(persistence.value)}
            </dd>
          </div>
          <div>
            <dt>Espace utilisé</dt>
            <dd>
              {formatBytes(usage.usageBytes)}
              {usage.quotaBytes === null ? '' : ` sur ${formatBytes(usage.quotaBytes)}`}
            </dd>
          </div>
          <div>
            <dt>Dernier export préparé</dt>
            <dd>
              {exported === null
                ? '—'
                : `${exported.fileName}, ${formatBytes(exported.bytes)}, le ${new Date(exported.preparedAt).toLocaleString('fr-FR')}`}
            </dd>
          </div>
        </dl>
        {exported !== null ? (
          <p class="muted">
            Ces métadonnées ne prouvent pas l’enregistrement définitif du fichier : ce choix appartient au
            navigateur.
          </p>
        ) : null}
        {offline.missing.length > 0 ? (
          <p class="notice">Ressources en défaut : {offline.missing.join(', ')}</p>
        ) : null}
        <div class="actions">
          <button
            type="button"
            onClick={() => {
              void retryOfflinePreparation();
            }}
          >
            {ACTIONS.retryOffline}
          </button>
        </div>
      </section>

      <section class="card">
        <h3 style="margin-top:0;">Mise à jour de l’application</h3>
        <p class="muted">
          La vérification interroge l’origine HTTPS de l’application et n’émet aucune requête vers un tiers.
        </p>
        <p>
          {update.waiting
            ? 'Une nouvelle version est installée et attend votre décision.'
            : 'Aucune version en attente.'}
        </p>
        {update.failure !== null ? <p class="notice notice--error">{update.failure}</p> : null}
        <div class="actions">
          <button
            type="button"
            disabled={update.checking}
            onClick={() => {
              void checkForUpdate();
            }}
          >
            {ACTIONS.checkUpdate}
          </button>
          <button
            type="button"
            class="primary"
            disabled={!update.waiting}
            onClick={() => {
              void activateUpdate();
            }}
          >
            {ACTIONS.activateUpdate}
          </button>
        </div>
      </section>

      <section class="card">
        <h3 style="margin-top:0;">{APP_NAME}</h3>
        <dl class="definition-list">
          <div>
            <dt>Version de l’application</dt>
            <dd>{APP_VERSION}</dd>
          </div>
          <div>
            <dt>Version de l’enveloppe applicative</dt>
            <dd>{offline.buildVersion ?? '—'}</dd>
          </div>
          <div>
            <dt>Endpoint actif</dt>
            <dd>
              {endpoint.operator} — {endpoint.url}
            </dd>
          </div>
        </dl>
        <p>{NOTICES.noAccount}</p>
        {/* Attribution OpenStreetMap : elle ne figure sur aucune autre surface. */}
        <p>
          <a href={NOTICES.attributionUrl} target="_blank" rel="noreferrer noopener">
            {NOTICES.attribution}
          </a>
        </p>
        <p>{NOTICES.licenseNotice}</p>
      </section>

      <section class="card">
        <h3 style="margin-top:0;">Limites connues et assumées</h3>
        <p class="muted">Ces limites sont des décisions de produit et non des défauts.</p>
        <dl class="definition-list">
          {KNOWN_LIMITS.map((entry) => (
            <div key={entry.limit}>
              <dt>{entry.limit}</dt>
              <dd>{entry.consequence}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section class="card">
        <h3 style="margin-top:0;">Réinitialisation</h3>
        <p class="muted">
          Chaque portée est distincte et irréversible. Aucune ne propose de fenêtre d’annulation.
        </p>
        <div class="actions actions--stacked">
          <button
            type="button"
            class="danger"
            onClick={() => {
              confirmReset('Réinitialiser les paramètres ?', CONFIRMATIONS.resetParameters, resetParameters);
            }}
          >
            {ACTIONS.resetParameters}
          </button>
          <button
            type="button"
            class="danger"
            onClick={() => {
              confirmReset('Réinitialiser le roadbook ?', CONFIRMATIONS.resetRoadbook, resetRoadbook);
            }}
          >
            {ACTIONS.resetRoadbook}
          </button>
          <button
            type="button"
            class="danger"
            onClick={() => {
              confirmReset('Réinitialisation complète ?', CONFIRMATIONS.resetAll, resetEverything);
            }}
          >
            {ACTIONS.resetAll}
          </button>
        </div>
      </section>
    </>
  );
}
